import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Input,
  Select,
  message,
  Tooltip,
  Row,
  Col,
  Statistic,
  Image,
  DatePicker,
  Popconfirm,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useAllListings } from '../hooks';
import AdminEditListingModal from './AdminEditListingModal';
import { adminService } from '../../api/services/adminService';
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CarOutlined,
  UserOutlined,
  DollarOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';

const { Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

interface Listing {
  id: string;
  title: string;
  make: string;
  model: string;
  year: number;
  price: number | string | null;
  mileage?: number | string | null;
  location?: string;
  status: string;
  user: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
  };
  images?: Array<{
    id: string;
    url: string;
    isPrimary: boolean;
    alt: string;
  }>;
  createdAt?: string;
  updatedAt?: string;
  viewCount?: number;
  favoriteCount?: number;
  paymentToken?: string | null;
  paymentAmount?: string | number | null;
  paymentDate?: string | null;
  submittedAt?: string | null;
}

interface AllListingsProps {
  onViewListing: (listing: any) => void;
}

const AllListings: React.FC<AllListingsProps> = ({ onViewListing }) => {
  const { t } = useTranslation();
  const {
    loading,
    listings,
    pagination,
    selectedRowKeys,
    setSelectedRowKeys,
    handleDelete,
    handleBulkDelete,
    handleFilterChange,
    handleTableChange,
    refetch,
  } = useAllListings();

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<any>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingListing, setEditingListing] = useState<any | null>(null);
  const [editLoading, setEditLoading] = useState(false);

  const handleEditClick = async (record: Listing) => {
    setEditLoading(true);
    try {
      const full = await adminService.getListingDetails(record.id);
      setEditingListing(full);
      setEditModalVisible(true);
    } catch {
      message.error(t('admin.listings.msg_load_failed'));
    } finally {
      setEditLoading(false);
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string; icon?: React.ReactNode }> = {
      DRAFT: { color: 'default', text: t('admin.listings.status_draft'), icon: <ClockCircleOutlined /> },
      UNDER_APPROVAL: { color: 'purple', text: t('admin.listings.status_under_approval'), icon: <ClockCircleOutlined /> },
      ACTIVE: { color: 'green', text: t('admin.listings.status_active'), icon: <CheckCircleOutlined /> },
      REJECTED: { color: 'red', text: t('admin.listings.status_rejected'), icon: <ExclamationCircleOutlined /> },
      SUSPENDED: { color: 'orange', text: t('admin.listings.status_suspended'), icon: <ExclamationCircleOutlined /> },
      SOLD: { color: 'default', text: t('admin.listings.status_sold'), icon: <CheckCircleOutlined /> },
    };

    const config = statusConfig[status] || { color: 'default', text: status, icon: undefined };

    if (status === 'UNDER_APPROVAL' || status === 'SUSPENDED') {
      return <Tag className="ant-tag-orange" icon={config.icon}>{config.text}</Tag>;
    }
    if (status === 'ACTIVE') {
      return <Tag color="success" icon={config.icon} style={{ border: 'none', background: 'rgba(82,196,26,0.1)', color: '#52c41a' }}>{config.text}</Tag>;
    }
    if (status === 'REJECTED') {
      return <Tag color="error" icon={config.icon} style={{ border: 'none', background: 'rgba(255,77,79,0.1)', color: '#ff4d4f' }}>{config.text}</Tag>;
    }
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: t('admin.listings.col_listing'),
      key: 'listing',
      width: 300,
      render: (record: Listing) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Image
            width={60}
            height={45}
            src={record.images?.[0]?.url || '/images/default-car.jpg'}
            fallback="/images/default-car.jpg"
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
          <div>
            <Text strong style={{ fontSize: '14px' }}>
              {record.title}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.year} {record.make} {record.model}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <EnvironmentOutlined /> {record.location}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: t('admin.listings.col_price'),
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (price: number | string | null) => {
        if (!price) return <Text type="secondary">{t('admin.listings.na')}</Text>;
        const numPrice = typeof price === 'string' ? parseFloat(price) : price;
        return <Text strong style={{ color: '#52c41a' }}>€{numPrice.toLocaleString('de-DE')}</Text>;
      },
    },
    {
      title: t('admin.listings.col_mileage'),
      dataIndex: 'mileage',
      key: 'mileage',
      width: 100,
      render: (mileage: number | string | null) => {
        if (!mileage) return <Text type="secondary">{t('admin.listings.na')}</Text>;
        const numMileage = typeof mileage === 'string' ? parseFloat(mileage) : mileage;
        return `${numMileage.toLocaleString()} km`;
      },
    },
    {
      title: t('admin.listings.col_user'),
      key: 'user',
      width: 150,
      render: (record: Listing) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserOutlined />
            <Text>{record.user.name}</Text>
          </div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.user.email}</Text>
        </div>
      ),
    },
    {
      title: t('admin.listings.col_status'),
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: t('admin.listings.col_stats'),
      key: 'stats',
      width: 120,
      render: (record: Listing) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <EyeOutlined style={{ fontSize: '12px' }} />
            <Text style={{ fontSize: '12px' }}>{record.viewCount || 0}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CheckCircleOutlined style={{ fontSize: '12px' }} />
            <Text style={{ fontSize: '12px' }}>{record.favoriteCount || 0}</Text>
          </div>
        </div>
      ),
    },
    {
      title: t('admin.listings.col_created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string | undefined) => {
        if (!date) return <Text type="secondary" style={{ fontSize: '12px' }}>{t('admin.listings.na')}</Text>;
        return <Text style={{ fontSize: '12px' }}>{new Date(date).toLocaleDateString()}</Text>;
      },
    },
    {
      title: t('admin.listings.col_actions'),
      key: 'actions',
      width: 120,
      render: (record: Listing) => (
        <Space>
          <Tooltip title={t('admin.listings.btn_view_details')}>
            <Button type="text" icon={<EyeOutlined />} onClick={() => onViewListing(record)} />
          </Tooltip>
          <Tooltip title={t('admin.listings.btn_edit')}>
            <Button type="text" icon={<EditOutlined />} loading={editLoading} onClick={() => handleEditClick(record)} />
          </Tooltip>
          <Popconfirm
            title={t('admin.listings.btn_delete')}
            description={t('admin.listings.delete_confirm_desc')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('admin.listings.delete_confirm_ok')}
            okType="danger"
            cancelText={t('admin.listings.delete_confirm_cancel')}
          >
            <Tooltip title={t('admin.listings.btn_delete_short')}>
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  const hasSelected = selectedRowKeys.length > 0;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Header Stats */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title={t('admin.listings.stat_total')}
              value={listings.length}
              prefix={<CarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title={t('admin.listings.stat_active')}
              value={listings.filter(item => item.status === 'ACTIVE').length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title={t('admin.listings.stat_pending')}
              value={listings.filter(item => item.status === 'UNDER_APPROVAL').length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title={t('admin.listings.stat_revenue')}
              value={listings.reduce((sum, item) => {
                const price = typeof item.price === 'string' ? parseFloat(item.price) : item.price || 0;
                return sum + price;
              }, 0)}
              prefix={<DollarOutlined />}
              suffix=" ALL"
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Actions */}
      <Card bordered={false} className="admin-filter-card">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Search
              placeholder={t('admin.listings.search_placeholder')}
              allowClear
              className="admin-filter-input"
              value={searchText}
              onChange={e => {
                const value = e.target.value;
                setSearchText(value);
                handleFilterChange({ q: value });
              }}
              onSearch={value => handleFilterChange({ q: value })}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder={t('admin.listings.filter_status')}
              style={{ width: '100%' }}
              className="admin-filter-select"
              value={statusFilter}
              onChange={v => {
                setStatusFilter(v);
                handleFilterChange({ status: v });
              }}
            >
              <Select.Option value="all">{t('admin.listings.filter_all_status')}</Select.Option>
              <Select.Option value="DRAFT">{t('admin.listings.status_draft')}</Select.Option>
              <Select.Option value="UNDER_APPROVAL">{t('admin.listings.status_under_approval')}</Select.Option>
              <Select.Option value="ACTIVE">{t('admin.listings.status_active')}</Select.Option>
              <Select.Option value="REJECTED">{t('admin.listings.status_rejected')}</Select.Option>
              <Select.Option value="SUSPENDED">{t('admin.listings.status_suspended')}</Select.Option>
              <Select.Option value="SOLD">{t('admin.listings.status_sold')}</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <RangePicker
              style={{ width: '100%' }}
              className="admin-filter-picker"
              placeholder={[t('admin.listings.date_start'), t('admin.listings.date_end')]}
              value={dateRange}
              onChange={dates => {
                setDateRange(dates);
                if (!dates || dates.length !== 2) {
                  handleFilterChange({ dateRange: null });
                } else {
                  const [start, end] = dates as any;
                  const formatted = [
                    start?.format?.('YYYY-MM-DD') || start,
                    end?.format?.('YYYY-MM-DD') || end,
                  ];
                  handleFilterChange({ dateRange: formatted as any });
                }
              }}
            />
          </Col>
          <Col xs={24} sm={6}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={refetch} className="admin-filter-btn">
                {t('admin.listings.btn_refresh')}
              </Button>
              {hasSelected && (
                <Space>
                  <Button
                    type="primary"
                    className="admin-filter-btn admin-filter-btn-primary"
                    onClick={() => message.info(t('admin.listings.msg_bulk_coming_soon'))}
                  >
                    {t('admin.listings.btn_approve_count', { count: selectedRowKeys.length })}
                  </Button>
                  <Button danger onClick={handleBulkDelete} className="admin-filter-btn">
                    {t('admin.listings.btn_delete_count', { count: selectedRowKeys.length })}
                  </Button>
                </Space>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Listings Table */}
      <Card
        title={<span style={{ fontSize: '18px', fontWeight: 700, color: '#262626' }}>{t('admin.listings.table_title_all', { count: listings.length })}</span>}
        className="admin-table-card"
        bordered={false}
        extra={
          <Space>
            {hasSelected && <Text type="secondary">{t('admin.listings.table_selected', { count: selectedRowKeys.length })}</Text>}
            <Button type="primary" icon={<ReloadOutlined />} onClick={refetch} className="ant-btn-primary">
              {t('admin.listings.btn_refresh')}
            </Button>
          </Space>
        }
      >
        <Table
          className="admin-table"
          columns={columns}
          dataSource={listings}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('admin.listings.page_summary', { from: range[0], to: range[1], total }),
            onChange: (page, pageSize) => {
              handleTableChange({ current: page, pageSize });
            },
            pageSizeOptions: ['5', '10', '20', '50'],
            defaultPageSize: 5,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>
      <AdminEditListingModal
        visible={editModalVisible}
        listing={editingListing}
        onClose={() => { setEditModalVisible(false); setEditingListing(null); }}
        onSaved={() => refetch()}
      />
    </Space>
  );
};

export { AllListings };
export default AllListings;
